import { useEffect, useMemo, useState } from "react";
import { FaLongArrowAltLeft, FaPlay } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { setTabData } from "../redux/slices/tabSlice";
import { FaArrowRightLong } from "react-icons/fa6";
import { whatnext } from "../helper/constant";
import { useTranslation } from "react-i18next";
import { ApiGet } from "../helper/axiosFunctions";
import { commonToast } from "../helper/Toast";
import WorkshopDetailQuestions from "../components/Workshop/WorkshopDetail/WorkshopDetailQuestions";
import WorkshopDetailSettings from "../components/Workshop/WorkshopDetail/WorkshopDetailSettings";
import TestCardsModal from "../components/Workshop/WorkshopDetail/TestCardsModal";
import Button from "../components/ui/Button";
import { deckStatusBadgeClass } from "../components/DeckCard";

export default function WorkshopDetailPage() {
  const { t } = useTranslation();
  const { id: deckId } = useParams();
  const dispatch = useDispatch();
  const [tab, setTab] = useState("questions");
  const [hide, setHide] = useState(() => {
    const savedHide = localStorage.getItem("creator_helper_hidden");
    return savedHide ? JSON.parse(savedHide) : false;
  });
  const [deckDetails, setDeckDetails] = useState(null);
  const [questionCount, setQuestionCount] = useState(0);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);

  const tabs = [
    { id: "questions", label: t("questions") },
    { id: "settings", label: t("Settings") },
    { id: "statistics", label: t("Statistics") },
    { id: "feedback", label: t("feedback").split(" ")[0] },
  ];

  const fetchDeckDetails = async () => {
    try {
      const response = await ApiGet(`/contents/decks/${deckId}`);
      setDeckDetails(response.data);
      setQuestionCount(response.data?.cards?.length || 0);
    } catch (error) {
      commonToast(
        error?.data?.errorDescription || error?.data?.error || error?.message,
        "error"
      );
    }
  };

  useEffect(() => {
    if (deckId) fetchDeckDetails();
  }, [deckId]);

  const toggleHide = () => {
    const next = !hide;
    setHide(next);
    localStorage.setItem("creator_helper_hidden", JSON.stringify(next));
  };

  const meta = useMemo(() => {
    const grades = (deckDetails?.schoolGrades || []).filter(
      (g) => g && String(g).trim()
    );
    return [
      deckDetails?.mainSubject,
      grades.length ? `${t("gradeLabel")}: ${grades.join(", ")}` : null,
      deckDetails?.language ? String(deckDetails.language).toUpperCase() : null,
    ]
      .filter(Boolean)
      .join(" · ");
  }, [deckDetails, t]);

  return (
    <div className="bg-[#F5F5F5] min-h-full">
      <div className="border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-[1400px] px-5 py-5 md:px-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-3">
              <Link
                to="/workshop"
                onClick={() => dispatch(setTabData("workshops"))}
                className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full border border-gray-200 text-gray-700 hover:bg-gray-50"
              >
                <FaLongArrowAltLeft />
              </Link>
              <div className="min-w-0 space-y-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="truncate text-xl font-bold text-gray-900">
                    {deckDetails?.deckName || "…"}
                  </h1>
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${deckStatusBadgeClass(deckDetails?.status || "PUBLISHED")}`}
                  >
                    {t(deckDetails?.status || "PUBLISHED")}
                  </span>
                </div>
                {meta ? (
                  <p className="truncate text-sm text-lightgray">{meta}</p>
                ) : null}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                onClick={() => setIsTestModalOpen(true)}
              >
                <FaPlay className="text-[10px]" /> {t("Test")}
              </Button>
              <Button variant="primary">{t("Publish")}</Button>
            </div>
          </div>

          <div className="mt-5 flex w-fit max-w-full items-center gap-1.5 overflow-auto rounded-full border border-gray-200 bg-gray-50 p-1.5">
            {tabs.map((item) => (
              <button
                key={item.id}
                type="button"
                disabled={!deckDetails}
                onClick={() => deckDetails && setTab(item.id)}
                className={`shrink-0 rounded-full px-4 py-2 text-[15px] transition-colors disabled:opacity-50 ${
                  item.id === tab
                    ? "bg-darkblue font-semibold text-white shadow-sm"
                    : "text-gray-600 hover:bg-white"
                }`}
              >
                {item.id === "questions"
                  ? `${item.label} (${questionCount})`
                  : item.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-5 py-4 md:px-4">
        <div className="flex items-start gap-4 lg:flex-col">
          <div className="min-w-0 flex-1">
            {tab === "questions" && (
              <WorkshopDetailQuestions
                deckDetails={deckDetails}
                onQuestionCountChange={setQuestionCount}
                onDeckDetailsRefresh={fetchDeckDetails}
              />
            )}
            {tab === "settings" && (
              <WorkshopDetailSettings deckDetails={deckDetails} />
            )}
            {(tab === "statistics" || tab === "feedback") && (
              <div className="rounded-2xl border border-gray-200 bg-white px-4 py-12 text-center text-sm text-gray-400">
                {tab === "statistics" ? t("Statistics") : t("feedback")}
              </div>
            )}
          </div>

          {!hide && (
            <aside className="w-64 shrink-0 rounded-2xl border border-gray-200 bg-white p-4 md:w-full">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h2 className="text-sm font-bold text-gray-900">{t("whatnext")}</h2>
                <button
                  type="button"
                  className="text-xs text-gray-500 underline hover:text-gray-700"
                  onClick={toggleHide}
                >
                  {t("hide")}
                </button>
              </div>
              <div className="space-y-3">
                {whatnext?.map(({ title, desc }, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[10px] font-bold text-gray-600">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-medium text-gray-800">{title}</h4>
                      <p className="mt-0.5 text-xs leading-relaxed text-lightgray">
                        {desc}
                      </p>
                    </div>
                    <FaArrowRightLong className="mt-1 shrink-0 text-gray-300" size={12} />
                  </div>
                ))}
              </div>
            </aside>
          )}
        </div>
      </div>

      <TestCardsModal
        open={isTestModalOpen}
        onClose={() => setIsTestModalOpen(false)}
        deckId={deckId}
        deckName={deckDetails?.deckName}
        deckCardsMeta={deckDetails?.cards}
      />
    </div>
  );
}
